import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { useUpdateEffect } from "react-use";
import { useRecoilValue } from "recoil";
import { surveyFocusIndex } from "../../states/atoms/surveyState";
import {
  Button,
  ButtonGroup,
  ButtonGroupItem,
  GhostButton,
  IconButton,
  LinkButton,
  Rating,
  TextArea,
  TextInput,
} from "../common/Forms";
import Icon from "../common/Icon";
import Message from "../common/Message";
import { OptionsAnswerComponent } from "./SurveyComponents";

const AddSurveyCard = (props) => {
  const focusIndex = useRecoilValue(surveyFocusIndex);
  const [q, setQ] = useState([]);
  const [qType, setType] = useState("text");
  const [qRequired, setRequired] = useState(true);
  const [options, setOptions] = useState([{ content: "" }]);
  const [addedInput, setAddedInput] = useState(false);
  const [editable, setEditable] = useState(props.editable);
  const [error, setError] = useState({ q: null, a: null });

  useEffect(() => {
    setQ(props.q.content);
    setType(props.q.type);
    setRequired(props.q.required);
    setOptions(props.options || [{ content: "" }]);
  }, [props.q.content, props.q.required, props.q.type, props.options]);

  useUpdateEffect(() => {
    if (focusIndex === props.n - 1 && editable && !props.editable) {
      const val = validate();
      if (val) {
        props.onAdd(val, props.n);
      }
    }
    setEditable(props.editable);
  }, [props.editable]);

  useUpdateEffect(() => {
    if (editable) {
      const val = validate();
      if (val) {
        props.onAdd(val, props.n, true);
      }
    }
  }, [focusIndex]);

  // useUpdateEffect(() => {
  //   if (qType === "rating") {
  //     setOptions([{ content: 10 }]);
  //   } else {
  //     setOptions([{ content: "" }]);
  //   }
  // }, [qType]);

  const handleQuestion = (e) => {
    if (e.target.value) {
      setError({ q: null });
    } else {
      setError({ q: "질문을 입력하세요." });
    }
    setQ(e.target.value.split(/\r|\r\n|\n/));
  };

  const handleType = (e) => {
    setError({ ...error, a: null });
    if (qType === e.target.id && qType === "checkbox") {
      setType("radio");
    } else {
      setType(e.target.id);
    }
  };

  const handleRequired = (e) => {
    setRequired(!qRequired);
  };

  const handleOption = (e) => {
    if (qType === "text" || qType === "textarea") {
      setOptions([{ content: e.target.value }]);
    }
  };

  const handleAddOptions =
    (bInput = false) =>
    () => {
      if (bInput) {
        setAddedInput(!addedInput);
      }
      const content = bInput ? { type: "text", content: "" } : "";
      setOptions([...options, { content }]);
    };

  const handleOptionsChange =
    (idx, bSub = false) =>
    (e) => {
      const newOne = [...options];
      const obj = newOne[idx];
      if (bSub) {
        if (typeof obj.content === "string") {
          const content = obj.content;
          newOne.splice(idx, 1, { content: { type: "text", content } });
        } else {
          newOne.splice(idx, 1, { content: obj.content.content });
        }
      } else {
        if (typeof obj.content === "string") {
          newOne.splice(idx, 1, { content: e.target.value });
        } else {
          newOne.splice(idx, 1, {
            content: { type: "text", content: e.target.value },
          });
        }
      }
      setOptions(newOne);
    };

  const handleOptionRemove = (idx) => () => {
    const newOne = [...options];
    const [rmvOne] = newOne.splice(idx, 1);
    if (typeof rmvOne === "object") {
      setAddedInput(false);
    }
    setOptions(newOne);
  };

  const handleClick = (e) => {
    if (!editable || (editable && e.target.id === props.id)) {
      props.onClick(props.n - 1);
    }
  };

  const validate = () => {
    if (!q || q.length === 0) {
      setError({ q: "질문을 입력하세요." });
      return false;
    }

    if (qType === "radio" || qType === "checkbox") {
      if (options.length === 0) {
        setError({ a: "보기를 입력하세요." });
        return false;
      }
    } else if (!options[0].content) {
      if (qType !== "rating") {
        setError({ a: "보기를 입력하세요." });
        return false;
      }
    }

    const id = uuidv4();
    return {
      id,
      key: id,
      q: {
        content: q,
        required: qRequired,
        type: qType,
      },
      options,
    };
  };

  const handleAdd = () => {
    const val = validate();
    if (val) {
      props.onAdd(val);
      props.onClick(props.n);
    }
  };

  const handleUp = () => {
    const val = validate();
    if (val) {
      props.onMove(val, -1);
    }
  };

  const handleDown = () => {
    const val = validate();
    if (val) {
      props.onMove(val, 1);
    }
  };

  return (
    <div
      id={props.id}
      className="flex flex-col items-center w-full gap-4 pb-4 bg-white"
      onClick={handleClick}
    >
      {!editable && (
        <div
          className={`flex flex-col self-stretch pb-4 px-2 rounded-2xl mx-4 bg-transparent`}
        >
          <div className="inline-flex gap-2">
            <p className="font-bold">{props.n}.</p>
            <div className="w-full font-semibold text-black">
              <>
                {(!q || q.length === 0) && (
                  <>
                    <span>질문을 입력하세요.</span>
                    <span className="ordinal text-red-600"> *</span>
                  </>
                )}
                {q &&
                  q.map((v, i) => (
                    <div key={`q_${props.n}_line_${i}`}>
                      <span className={i > 0 ? "block" : ""}>{v}</span>
                      {i === 0 && qRequired && (
                        <span className="ordinal text-red-600"> *</span>
                      )}
                    </div>
                  ))}
                <fieldset>
                  <div className="mt-4 ml-4 space-y-4 w-full">
                    {options &&
                      options.map((option, j) => {
                        const { content } = option;
                        return (
                          <OptionsAnswerComponent
                            {...{
                              key: `options_${props.n}_${j}`,
                              name: `options_${props.n}_${j}`,
                              type: qType,
                              content,
                              index: props.n,
                              readOnly: true,
                            }}
                          />
                        );
                      })}
                  </div>
                </fieldset>
              </>
            </div>
          </div>
        </div>
      )}
      {editable && (
        <div
          className={`flex flex-col self-stretch pb-4 px-2 rounded-2xl mx-4 bg-zinc-200`}
        >
          <div className="inline-flex self-end p-2 gap-2 opacity-70">
            <IconButton disabled={props.isLast} onClick={handleDown}>
              <Icon name="arrow-down" size="sm" />
            </IconButton>
            <IconButton disabled={props.isFirst} onClick={handleUp}>
              <Icon name="arrow-up" size="sm" />
            </IconButton>
            <IconButton className="ml-2" onClick={props.onRemove}>
              <Icon name="trash" size="sm" className="text-error" />
            </IconButton>
          </div>
          <div className="inline-flex gap-2">
            <p className="font-bold">{props.n}.</p>
            <div className="flex flex-col gap-4 w-full">
              <TextArea
                id="qText"
                placeholder="질문을 입력하세요."
                onChange={handleQuestion}
                value={q.join("\n")}
                readOnly={!editable}
                className="flex-1 w-full p-2 shadow-sm text-sm"
              />
              {!!error.q && <Message type="error">{error.q}</Message>}
              <div className="inline-flex w-full gap-4">
                <ButtonGroup label="답변">
                  {[
                    { text: "단답형" },
                    { textarea: "서술형" },
                    { rating: "별점형" },
                    { radio: "선택형" },
                  ].map((item) => {
                    const [k, v] = Object.entries(item)[0];
                    return (
                      <ButtonGroupItem
                        {...{
                          active:
                            k === qType ||
                            (qType === "checkbox" && k === "radio"),
                          disabled: !editable,
                          id: k,
                          key: k,
                          onClick: handleType,
                        }}
                      >
                        {v}
                      </ButtonGroupItem>
                    );
                  })}
                </ButtonGroup>
                <div className="inline-flex gap-2 items-center">
                  <p className="text-sm font-bold">필수</p>
                  <div className="form-control w-16">
                    <label className="cursor-pointer label">
                      <input
                        className="toggle toggle-accent"
                        type="checkbox"
                        checked={qRequired}
                        onChange={handleRequired}
                        readOnly={!editable}
                      />
                    </label>
                  </div>
                </div>
                {(qType === "radio" || qType === "checkbox") && (
                  <div className="inline-flex gap-2 items-center">
                    <p className="text-sm font-bold">복수</p>
                    <div className="form-control w-16">
                      <label className="cursor-pointer label">
                        <input
                          id="checkbox"
                          type="checkbox"
                          className="toggle toggle-accent"
                          onChange={handleType}
                          checked={qType === "checkbox"}
                          readOnly={!editable}
                        />
                      </label>
                    </div>
                  </div>
                )}
              </div>
              {qType === "text" && (
                <TextInput
                  className="p-2"
                  onChange={handleOption}
                  placeholder="placeholder를 입력하세요."
                  readOnly={!editable}
                  type="text"
                />
              )}
              {qType === "textarea" && (
                <TextArea
                  placeholder="placeholder를 입력하세요."
                  className="flex-1 w-full p-2 shadow-sm text-sm"
                  onChange={handleOption}
                  readOnly={!editable}
                />
              )}
              {qType === "rating" && (
                <>
                  <p className="text-xs text-red-300 mb-[-1rem]">
                    {"> 설문 응답시, 아래와 같이 보여집니다."}
                  </p>
                  <div className="rating gap-1">
                    <Rating
                      {...{
                        length: 10,
                        value: 5,
                        id: "rating-eg",
                        readonly: true,
                      }}
                    />
                  </div>
                </>
              )}
              {qType === "radio" && (
                <div className="flex flex-col w-full gap-1">
                  {options.map(({ content }, j) => (
                    <div
                      className="inline-flex w-full gap-2 items-center"
                      key={`q${props.n}_options_${j}`}
                    >
                      <input
                        type="radio"
                        className="h-4 w-4 border-zinc-400 bg-white"
                        checked={j === 0}
                        readOnly
                      />
                      <TextInput
                        className={`w-full px-2 py-1 ${
                          typeof content === "string"
                            ? ""
                            : "bg-zinc-300 placeholder-white"
                        }`}
                        onChange={handleOptionsChange(j)}
                        readOnly={!editable}
                        type="text"
                        value={
                          typeof content === "string"
                            ? content
                            : content.content
                        }
                      />
                      <GhostButton onClick={handleOptionRemove(j)}>
                        <Icon
                          name="x-circle"
                          className="text-zinc-600"
                          size="sm"
                        />
                      </GhostButton>
                    </div>
                  ))}
                  {editable && (
                    <div className="flex flex-col mt-2">
                      <LinkButton onClick={handleAddOptions()}>
                        <Icon name="plus" size="sm" />
                        보기 추가
                      </LinkButton>
                      {!addedInput && (
                        <LinkButton onClick={handleAddOptions(true)}>
                          <Icon name="plus" size="sm" />
                          직접 입력 보기 추가
                        </LinkButton>
                      )}
                    </div>
                  )}
                  {!!error.a && <Message type="error">{error.a}</Message>}
                </div>
              )}
              {qType === "checkbox" && (
                <div className="flex flex-col w-full gap-1">
                  {options.map(({ content }, j) => {
                    return (
                      <div
                        className="inline-flex w-full gap-2 items-center"
                        key={`q${props.n}_options_${j}`}
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 border-zinc-400 bg-white"
                          checked={j === 0}
                          readOnly
                        />
                        <TextInput
                          className={`w-full px-2 py-1 ${
                            typeof content === "string"
                              ? ""
                              : "bg-zinc-300 placeholder-white"
                          }`}
                          onChange={handleOptionsChange(j)}
                          readOnly={!editable}
                          value={
                            typeof content === "string"
                              ? content
                              : content.content
                          }
                        />
                        <GhostButton onClick={handleOptionRemove(j)}>
                          <Icon
                            name="x-circle"
                            size="sm"
                            className="text-zinc-600"
                          />
                        </GhostButton>
                      </div>
                    );
                  })}
                  {editable && (
                    <div className="flex flex-col mt-2">
                      <LinkButton onClick={handleAddOptions()}>
                        <Icon name="plus" size="sm" />
                        보기 추가
                      </LinkButton>
                      {!addedInput && (
                        <LinkButton onClick={handleAddOptions(true)}>
                          <Icon name="plus" size="sm" />
                          직접 입력 보기 추가
                        </LinkButton>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {editable && (
        <Button
          id="survey_card_add_btn"
          type="primary"
          size="sm"
          className="self-stretch mx-4"
          onClick={handleAdd}
        >
          <Icon name="plus" size="sm" />
          추가
        </Button>
      )}
    </div>
  );
};

export default AddSurveyCard;
